// context/AuthContext.js
// MERGED: Original dual auth (member/vendor) + Firebase Authentication
// ============================================
// Keeps all existing functionality AND adds Firebase Google Sign-In
// UPDATED Dec 31, 2025: Fixed vendor field pass-through for 4.12
// UPDATED Jan 15, 2026: B16 - Added auto logout after 30 min inactivity
// FIXED Jan 15, 2026: Stabilized effect dependencies to fix navigation bug
// ============================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { jwtDecode } from 'jwt-decode';
import { getDemoUserDataWithSkills } from '../utils/groupData';

// Firebase imports
import { 
  signInWithPopup,
  signInWithEmailAndPassword as firebaseSignInWithEmail,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { TOKEN_CONFIG } from '../utils/tokenEconomy';

// Storage keys
const MEMBER_AUTH_KEY = 'uniledge_member_auth';
const VENDOR_AUTH_KEY = 'uniledge_vendor_auth';
const REFERRAL_STORAGE_KEY = 'uniledge_referral_code';
const LAST_ACTIVITY_KEY = 'uniledge_last_activity';

// B16: Inactivity timeout configuration (30 minutes)
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes in milliseconds
const ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

// API endpoints for Firebase sync
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const API_ENDPOINTS = {
  SYNC_USER: `${API_BASE_URL}/auth/sync`,
  VERIFY_TOKEN: `${API_BASE_URL}/auth/verify`,
  DELETE_ACCOUNT: `${API_BASE_URL}/auth/delete`,
};

// Define GraphQL operations
const LOGIN_USER = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user {
        id
        name
        email
        role
      }
    }
  }
`;

const REGISTER_USER = gql`
  mutation Register($input: CreateUserInput!) {
    register(input: $input) {
      token
      user {
        id
        name
        email
        role
      }
    }
  }
`;

// Create the auth context
const AuthContext = createContext({
  user: null,
  memberAuth: null,
  vendorAuth: null,
  firebaseUser: null,
  login: () => {},
  register: () => {},
  logout: () => {},
  memberLogin: () => {},
  vendorLogin: () => {},
  memberLogout: () => {},
  vendorLogout: () => {},
  // Firebase methods
  signInWithGoogle: () => {},
  signInWithEmail: () => {},
  registerWithEmail: () => {},
  resetPassword: () => {},
  deleteAccount: () => {},
  // State
  isLoggedIn: false,
  loading: false,
  error: null,
  isNewUser: false,
  // Helpers
  refreshUser: () => {},
  getActiveUser: () => {},
  getIdToken: () => {},
  saveReferralCode: () => {},
  clearError: () => {},
  clearNewUserFlag: () => {},
  // B16: Inactivity
  resetActivityTimer: () => {},
  getTimeUntilLogout: () => {},
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component to wrap the app
export const AuthProvider = ({ children }) => {
  // Original state
  const [memberAuth, setMemberAuth] = useState(null);
  const [vendorAuth, setVendorAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Firebase state
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  
  // B16: Use refs for inactivity tracking to avoid effect dependency issues
  const inactivityTimerRef = useRef(null);
  const activityCheckIntervalRef = useRef(null);
  const lastActivityTimeRef = useRef(Date.now());
  const memberAuthRef = useRef(null);
  const vendorAuthRef = useRef(null);
  const firebaseUserRef = useRef(null);
  
  const client = useApolloClient();
  
  // Login mutation
  const [loginMutation] = useMutation(LOGIN_USER);
  
  // Register mutation
  const [registerMutation] = useMutation(REGISTER_USER);

  // Keep refs in sync with state
  useEffect(() => {
    memberAuthRef.current = memberAuth;
  }, [memberAuth]);

  useEffect(() => {
    vendorAuthRef.current = vendorAuth;
  }, [vendorAuth]);

  useEffect(() => {
    firebaseUserRef.current = firebaseUser;
  }, [firebaseUser]);

  // ============================================
  // B16: INACTIVITY TIMEOUT FUNCTIONS (FIXED)
  // Using refs to avoid dependency cycles
  // ============================================

  // Update last activity timestamp - stable function using ref
  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    lastActivityTimeRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  }, []);

  // Reset the inactivity timer (public API)
  const resetActivityTimer = useCallback(() => {
    updateLastActivity();
  }, [updateLastActivity]);

  // Get time remaining until auto-logout (in seconds)
  const getTimeUntilLogout = useCallback(() => {
    const storedLastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    const lastActivity = storedLastActivity ? parseInt(storedLastActivity, 10) : Date.now();
    const elapsed = Date.now() - lastActivity;
    const remaining = Math.max(0, INACTIVITY_TIMEOUT_MS - elapsed);
    return Math.floor(remaining / 1000);
  }, []);

  // Internal logout functions - using refs to avoid stale closures
  const performMemberLogout = useCallback((dueToInactivity = false) => {
    setMemberAuth(null);
    memberAuthRef.current = null;
    localStorage.removeItem(MEMBER_AUTH_KEY);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    
    if (!vendorAuthRef.current) {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userType');
    }
    
    // Also sign out of Firebase if signed in
    if (firebaseUserRef.current) {
      firebaseSignOut(auth).catch(console.error);
      setFirebaseUser(null);
      firebaseUserRef.current = null;
    }
    
    client.resetStore();

    // Show notification if due to inactivity
    if (dueToInactivity && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:inactivity-logout', {
        detail: { message: 'You have been logged out due to inactivity.' }
      }));
    }
  }, [client]);

  const performVendorLogout = useCallback((dueToInactivity = false) => {
    setVendorAuth(null);
    vendorAuthRef.current = null;
    localStorage.removeItem(VENDOR_AUTH_KEY);
    localStorage.removeItem('currentVendor');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    
    if (!memberAuthRef.current) {
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userType');
    }

    if (dueToInactivity && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:inactivity-logout', {
        detail: { message: 'You have been logged out due to inactivity.' }
      }));
    }
  }, []);

  // Check if user should be logged out due to inactivity
  // Uses refs to avoid recreating on every state change
  const checkInactivity = useCallback(() => {
    // Only check if user is logged in (use refs for current values)
    if (!memberAuthRef.current && !vendorAuthRef.current) return;

    const storedLastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!storedLastActivity) return;

    const lastActivity = parseInt(storedLastActivity, 10);
    const elapsed = Date.now() - lastActivity;

    if (elapsed >= INACTIVITY_TIMEOUT_MS) {
      console.log('[AUTH] Auto-logout due to inactivity');
      
      // Perform logout
      if (memberAuthRef.current) {
        performMemberLogout(true);
      }
      if (vendorAuthRef.current) {
        performVendorLogout(true);
      }
    }
  }, [performMemberLogout, performVendorLogout]);

  // Set up activity listeners - STABLE effect with minimal dependencies
  useEffect(() => {
    // Determine if we should track activity
    const shouldTrack = memberAuth !== null || vendorAuth !== null;
    
    if (!shouldTrack) {
      // Clear any existing timers when logged out
      if (activityCheckIntervalRef.current) {
        clearInterval(activityCheckIntervalRef.current);
        activityCheckIntervalRef.current = null;
      }
      return;
    }

    // Activity events to track
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttled activity handler - uses ref to avoid dependency
    let lastUpdateTime = 0;
    const throttleMs = 30000; // 30 seconds

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdateTime >= throttleMs) {
        lastUpdateTime = now;
        // Directly update localStorage and ref, no state change
        lastActivityTimeRef.current = now;
        localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
      }
    };

    // Add event listeners with passive flag for performance
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Handle visibility change (tab becomes active)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if we should logout due to being inactive while tab was hidden
        checkInactivity();
        // If still logged in, update activity
        if (memberAuthRef.current || vendorAuthRef.current) {
          lastActivityTimeRef.current = Date.now();
          localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Set up periodic inactivity check (every minute)
    activityCheckIntervalRef.current = setInterval(checkInactivity, ACTIVITY_CHECK_INTERVAL_MS);

    // Initialize last activity
    updateLastActivity();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (activityCheckIntervalRef.current) {
        clearInterval(activityCheckIntervalRef.current);
        activityCheckIntervalRef.current = null;
      }
    };
  }, [memberAuth, vendorAuth, checkInactivity, updateLastActivity]); // Only re-run when auth state changes

  // Check for session expiry on mount (handle page refresh after long absence)
  useEffect(() => {
    const storedLastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (storedLastActivity) {
      const lastActivity = parseInt(storedLastActivity, 10);
      const elapsed = Date.now() - lastActivity;
      
      // If more than timeout has passed since last activity, clear session
      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        console.log('[AUTH] Session expired due to inactivity during page close');
        localStorage.removeItem(MEMBER_AUTH_KEY);
        localStorage.removeItem(VENDOR_AUTH_KEY);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userType');
        localStorage.removeItem('currentVendor');
      }
    }
  }, []);

  // ============================================
  // ORIGINAL FUNCTIONS (kept as-is)
  // ============================================

  // Determine active user based on current route
  const getActiveUser = () => {
    const pathname = window.location.pathname;
    
    if (pathname.startsWith('/vendor')) {
      return vendorAuth;
    }
    return memberAuth;
  };

  // Function to load user from localStorage
  const loadUserFromStorage = () => {
    // Check for inactivity timeout first
    const storedLastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (storedLastActivity) {
      const elapsed = Date.now() - parseInt(storedLastActivity, 10);
      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        // Clear everything and don't load
        localStorage.removeItem(MEMBER_AUTH_KEY);
        localStorage.removeItem(VENDOR_AUTH_KEY);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userType');
        localStorage.removeItem('currentVendor');
        return;
      }
    }

    // Load member auth
    const savedMemberAuth = localStorage.getItem(MEMBER_AUTH_KEY);
    if (savedMemberAuth) {
      try {
        const memberData = JSON.parse(savedMemberAuth);
        const demoUserData = getDemoUserDataWithSkills();
        const userWithFullData = {
          ...demoUserData,
          ...memberData,
          id: memberData.id || 'user123',
          userType: 'member'
        };
        setMemberAuth(userWithFullData);
        memberAuthRef.current = userWithFullData;
      } catch (err) {
        console.error('Error parsing member auth:', err);
        localStorage.removeItem(MEMBER_AUTH_KEY);
      }
    }

    // Load vendor auth
    const savedVendorAuth = localStorage.getItem(VENDOR_AUTH_KEY);
    if (savedVendorAuth) {
      try {
        const vendorData = JSON.parse(savedVendorAuth);
        vendorData.userType = 'vendor';
        setVendorAuth(vendorData);
        vendorAuthRef.current = vendorData;
      } catch (err) {
        console.error('Error parsing vendor auth:', err);
        localStorage.removeItem(VENDOR_AUTH_KEY);
      }
    }

    // Legacy support - check old auth methods
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const userType = localStorage.getItem('userType');
    
    if (isAuthenticated && !savedMemberAuth && !savedVendorAuth) {
      if (userType === 'vendor') {
        const currentVendor = localStorage.getItem('currentVendor');
        if (currentVendor) {
          try {
            const vendor = JSON.parse(currentVendor);
            vendor.userType = 'vendor';
            setVendorAuth(vendor);
            vendorAuthRef.current = vendor;
            localStorage.setItem(VENDOR_AUTH_KEY, JSON.stringify(vendor));
          } catch (e) {
            console.error('Error migrating vendor data:', e);
          }
        }
      } else {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            const demoUserData = getDemoUserDataWithSkills();
            const userWithFullData = {
              ...demoUserData,
              ...userData,
              id: userData.id || 'user123',
              userType: 'member'
            };
            setMemberAuth(userWithFullData);
            memberAuthRef.current = userWithFullData;
            localStorage.setItem(MEMBER_AUTH_KEY, JSON.stringify(userWithFullData));
          } catch (err) {
            console.error('Error migrating member data:', err);
          }
        }
      }
    }

    // Update last activity on successful load
    if (savedMemberAuth || savedVendorAuth) {
      updateLastActivity();
    }
  };

  // Member login function (original)
  const memberLogin = async (email, password) => {
    setLoading(true);
    setError(null);
    
    // For demo mode, check if it's demo credentials
    if (email === 'demo@example.com' || email === 'jane@example.com') {
      const demoUserData = getDemoUserDataWithSkills();
      const userWithTrust = {
        ...demoUserData,
        id: 'user123',
        name: email === 'jane@example.com' ? 'Jane Smith' : 'Demo User',
        email: email,
        userType: 'member'
      };
      
      const demoToken = 'demo_token_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      
      localStorage.setItem(MEMBER_AUTH_KEY, JSON.stringify(userWithTrust));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userType', 'member');
      localStorage.setItem('user', JSON.stringify(userWithTrust));
      localStorage.setItem('token', demoToken);
      
      setMemberAuth(userWithTrust);
      memberAuthRef.current = userWithTrust;
      updateLastActivity();
      setLoading(false);
      return userWithTrust;
    }
    
    // Real login flow
    try {
      const { data } = await loginMutation({
        variables: { email, password }
      });
      
      const demoUserData = getDemoUserDataWithSkills();
      const userWithTrust = {
        ...data.login.user,
        id: data.login.user.id || 'user123',
        userType: 'member',
        trust: demoUserData.trust,
        skills: demoUserData.skills,
        availability: demoUserData.availability,
        stats: demoUserData.stats,
        reliabilityScore: demoUserData.reliabilityScore,
        tokens: demoUserData.tokens,
        location: demoUserData.location
      };
      
      localStorage.setItem(MEMBER_AUTH_KEY, JSON.stringify(userWithTrust));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userType', 'member');
      localStorage.setItem('user', JSON.stringify(userWithTrust));
      localStorage.setItem('token', data.login.token);
      
      setMemberAuth(userWithTrust);
      memberAuthRef.current = userWithTrust;
      updateLastActivity();
      return userWithTrust;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Vendor login function (original)
  const vendorLogin = async (vendorData) => {
    setLoading(true);
    setError(null);
    
    try {
      const vendor = {
        ...vendorData,
        userType: 'vendor'
      };
      
      localStorage.setItem(VENDOR_AUTH_KEY, JSON.stringify(vendor));
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userType', 'vendor');
      localStorage.setItem('currentVendor', JSON.stringify(vendor));
      
      setVendorAuth(vendor);
      vendorAuthRef.current = vendor;
      updateLastActivity();
      return vendor;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Member logout function (public API)
  const memberLogout = () => {
    performMemberLogout(false);
  };

  // Vendor logout function (public API)
  const vendorLogout = () => {
    performVendorLogout(false);
  };

  // Legacy login function (original)
  const login = async (email, password) => {
    return memberLogin(email, password);
  };

  // Legacy logout function (original)
  const logout = () => {
    const activeUser = getActiveUser();
    if (activeUser?.userType === 'vendor') {
      vendorLogout();
    } else {
      memberLogout();
    }
  };

  // Register function (original GraphQL)
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data } = await registerMutation({
        variables: { input: userData }
      });
      
      const demoUserData = getDemoUserDataWithSkills();
      const userWithTrust = {
        ...data.register.user,
        id: data.register.user.id || 'user123',
        userType: 'member',
        trust: {
          score: 3.0,
          level: "Building",
          breakdown: {
            groupParticipation: 3.0,
            jobCompletion: 3.0,
            peerRatings: 3.0,
            skillVerification: 3.0,
            responseTime: 3.0
          },
          completedJobs: 0,
          totalRatings: 0,
          badges: [],
          history: [],
          lastUpdated: new Date()
        },
        skills: {
          verified: [],
          selfReported: [],
          endorsements: []
        },
        availability: {
          weekends: false,
          evenings: false,
          preferredHours: [],
          maxHoursPerWeek: 0
        },
        stats: {
          groupsJoined: 0,
          groupsCompleted: 0,
          bulkOrders: 0,
          groupsCreated: 0,
          totalSaved: 0,
          jobsCompleted: 0,
          totalEarned: 0
        },
        reliabilityScore: {
          overall: 3.0,
          components: {
            groupCompletion: 3.0,
            paymentReliability: 3.0,
            gigCompletion: 3.0,
            responseTime: 3.0,
            communityRating: 3.0
          }
        },
        tokens: TOKEN_CONFIG.WELCOME_BONUS,
        location: demoUserData.location
      };
      
      localStorage.setItem(MEMBER_AUTH_KEY, JSON.stringify(userWithTrust));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userType', 'member');
      localStorage.setItem('user', JSON.stringify(userWithTrust));
      localStorage.setItem('token', data.register.token);
      
      setMemberAuth(userWithTrust);
      memberAuthRef.current = userWithTrust;
      setIsNewUser(true);
      updateLastActivity();
      return userWithTrust;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh user data from localStorage (original)
  const refreshUser = () => {
    loadUserFromStorage();
  };

  // ============================================
  // FIREBASE FUNCTIONS (new)
  // ============================================

  // Referral code helpers
  const getReferralCodeFromStorage = () => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(REFERRAL_STORAGE_KEY) || 
           sessionStorage.getItem(REFERRAL_STORAGE_KEY) ||
           null;
  };
  
  const saveReferralCode = (code) => {
    if (typeof window === 'undefined' || !code) return;
    localStorage.setItem(REFERRAL_STORAGE_KEY, code);
    sessionStorage.setItem(REFERRAL_STORAGE_KEY, code);
  };
  
  const clearReferralCodeFromStorage = () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  };

  // Sync Firebase user with backend
  const syncFirebaseUserWithBackend = useCallback(async (fbUser, additionalData = {}) => {
    if (!fbUser) return null;
    
    try {
      const idToken = await fbUser.getIdToken();
      
      const userData = {
        firebaseUid: fbUser.uid,
        email: fbUser.email,
        displayName: fbUser.displayName || additionalData.displayName || null,
        photoURL: fbUser.photoURL || null,
        emailVerified: fbUser.emailVerified,
        provider: fbUser.providerData[0]?.providerId || 'email',
        referralCode: additionalData.referralCode || getReferralCodeFromStorage(),
        isNewSignup: additionalData.isNewSignup || false,
        ...additionalData
      };
      
      const response = await fetch(API_ENDPOINTS.SYNC_USER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to sync user with backend');
      }
      
      const { user: backendUser, token: jwtToken, isNew, tokensAwarded } = await response.json();
      
      // Save JWT token for Apollo client
      if (jwtToken) {
        localStorage.setItem('token', jwtToken);
      }
      
      if (isNew) {
        setIsNewUser(true);
        clearReferralCodeFromStorage();
      }
      
      return {
        ...backendUser,
        tokensAwarded,
        isNew
      };
      
    } catch (err) {
      console.error('Error syncing user with backend:', err);
      
      // Return minimal user object if backend sync fails
      return {
        id: fbUser.uid,
        email: fbUser.email,
        displayName: fbUser.displayName,
        photoURL: fbUser.photoURL,
        emailVerified: fbUser.emailVerified,
        _backendSyncFailed: true
      };
    }
  }, []);

  // Convert Firebase user to member auth format
  // UPDATED: Now includes vendor fields from backend
  const firebaseUserToMemberAuth = (fbUser, backendUser = null) => {
    const demoUserData = getDemoUserDataWithSkills();
    
    return {
      ...demoUserData,
      id: backendUser?.id || backendUser?.userId || fbUser.uid,
      name: fbUser.displayName || backendUser?.displayName || backendUser?.profile?.name || fbUser.email?.split('@')[0],
      email: fbUser.email,
      photoURL: fbUser.photoURL,
      userType: 'member',
      firebaseUid: fbUser.uid,
      emailVerified: fbUser.emailVerified,
      tokens: backendUser?.tokenBalance || backendUser?.wallet?.tokenBalance || TOKEN_CONFIG.WELCOME_BONUS,
      trust: backendUser?.trust || demoUserData.trust,
      stats: backendUser?.stats || demoUserData.stats,
      // Vendor fields - pass through from backend (4.12 fix)
      userTypes: backendUser?.userTypes || [],
      isVendor: backendUser?.userTypes?.includes('vendor') || false,
      vendorStatus: backendUser?.profile?.verificationStatus || backendUser?.vendorStatus || null,
      businessName: backendUser?.businessName || backendUser?.profile?.name || null,
      vendorProfile: backendUser?.vendorProfile || null,
    };
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const fbUser = result.user;
      
      const isNewSignup = result._tokenResponse?.isNewUser || false;
      
      // Sync with backend
      const backendUser = await syncFirebaseUserWithBackend(fbUser, { 
        isNewSignup,
        provider: 'google.com'
      });
      
      // Convert to member auth format
      const memberData = firebaseUserToMemberAuth(fbUser, backendUser);
      
      // Save to localStorage
      localStorage.setItem(MEMBER_AUTH_KEY, JSON.stringify(memberData));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userType', 'member');
      localStorage.setItem('user', JSON.stringify(memberData));
      
      setMemberAuth(memberData);
      memberAuthRef.current = memberData;
      setFirebaseUser(fbUser);
      firebaseUserRef.current = fbUser;
      updateLastActivity();
      
      return { 
        success: true, 
        user: memberData,
        isNew: backendUser?.isNew || isNewSignup,
        tokensAwarded: backendUser?.tokensAwarded || (isNewSignup ? TOKEN_CONFIG.WELCOME_BONUS : 0)
      };
      
    } catch (err) {
      console.error('Google sign-in error:', err);
      
      let errorMessage = 'Failed to sign in with Google';
      
      switch (err.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in cancelled. Please try again.';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Pop-up was blocked. Please allow pop-ups and try again.';
          break;
        case 'auth/unauthorized-domain':
          errorMessage = 'This domain is not authorized. Please contact support.';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email. Try signing in with a different method.';
          break;
        default:
          errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
      
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Email (Firebase first, then fallback to GraphQL)
  const signInWithEmail = async (email, password) => {
    setError(null);
    setLoading(true);
    
    try {
      // Try Firebase first
      const result = await firebaseSignInWithEmail(auth, email, password);
      const fbUser = result.user;
      
      const backendUser = await syncFirebaseUserWithBackend(fbUser, {
        isNewSignup: false,
        provider: 'password'
      });
      
      const memberData = firebaseUserToMemberAuth(fbUser, backendUser);
      
      localStorage.setItem(MEMBER_AUTH_KEY, JSON.stringify(memberData));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userType', 'member');
      localStorage.setItem('user', JSON.stringify(memberData));
      
      setMemberAuth(memberData);
      memberAuthRef.current = memberData;
      setFirebaseUser(fbUser);
      firebaseUserRef.current = fbUser;
      updateLastActivity();
      
      return { success: true, user: memberData };
      
    } catch (err) {
      console.error('Firebase email sign-in error:', err);
      
      // If Firebase fails (user not in Firebase), try GraphQL/MongoDB login
      if (err.code === 'auth/user-not-found' || 
          err.code === 'auth/invalid-credential' ||
          err.code === 'auth/wrong-password') {
        
        console.log('Firebase user not found, trying GraphQL login...');
        
        try {
          // Fall back to original GraphQL login
          const user = await memberLogin(email, password);
          return { success: true, user };
        } catch (graphqlErr) {
          console.error('GraphQL login also failed:', graphqlErr);
          setError('Invalid email or password');
          return { success: false, error: 'Invalid email or password' };
        }
      }
      
      // Handle other Firebase errors
      let errorMessage = 'Failed to sign in';
      
      switch (err.code) {
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact support.';
          break;
        default:
          errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
      
    } finally {
      setLoading(false);
    }
  };

  // Register with Email (Firebase)
  const registerWithEmail = async (email, password, displayName = null, referralCode = null) => {
    setError(null);
    setLoading(true);
    
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const fbUser = result.user;
      
      if (displayName) {
        await updateProfile(fbUser, { displayName });
      }
      
      try {
        await sendEmailVerification(fbUser);
      } catch (verifyErr) {
        console.warn('Failed to send verification email:', verifyErr);
      }
      
      if (referralCode) {
        saveReferralCode(referralCode);
      }
      
      const backendUser = await syncFirebaseUserWithBackend(fbUser, {
        isNewSignup: true,
        displayName,
        referralCode: referralCode || getReferralCodeFromStorage(),
        provider: 'password'
      });
      
      const memberData = firebaseUserToMemberAuth(fbUser, backendUser);
      
      localStorage.setItem(MEMBER_AUTH_KEY, JSON.stringify(memberData));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userType', 'member');
      localStorage.setItem('user', JSON.stringify(memberData));
      
      setMemberAuth(memberData);
      memberAuthRef.current = memberData;
      setFirebaseUser(fbUser);
      firebaseUserRef.current = fbUser;
      setIsNewUser(true);
      updateLastActivity();
      
      return { 
        success: true, 
        user: memberData,
        isNew: true,
        tokensAwarded: backendUser?.tokensAwarded || TOKEN_CONFIG.WELCOME_BONUS
      };
      
    } catch (err) {
      console.error('Registration error:', err);
      
      let errorMessage = 'Failed to create account';
      
      switch (err.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account with this email already exists';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password must be at least 6 characters';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address';
          break;
        default:
          errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
      
    } finally {
      setLoading(false);
    }
  };

  // Password reset
  const resetPassword = async (email) => {
    setError(null);
    
    try {
      await sendPasswordResetEmail(auth, email);
      return { 
        success: true, 
        message: 'Password reset email sent. Check your inbox.' 
      };
      
    } catch (err) {
      console.error('Password reset error:', err);
      
      let errorMessage = 'Failed to send reset email';
      
      switch (err.code) {
        case 'auth/user-not-found':
          return { success: true, message: 'If an account exists, a reset email has been sent.' };
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later.';
          break;
        default:
          errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Delete account
  const deleteAccount = async (password = null) => {
    setError(null);
    
    if (!firebaseUser) {
      return { success: false, error: 'No Firebase user logged in' };
    }
    
    try {
      if (password && firebaseUser.providerData[0]?.providerId === 'password') {
        const credential = EmailAuthProvider.credential(firebaseUser.email, password);
        await reauthenticateWithCredential(firebaseUser, credential);
      }
      
      try {
        const idToken = await firebaseUser.getIdToken();
        await fetch(API_ENDPOINTS.DELETE_ACCOUNT, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
      } catch (backendErr) {
        console.warn('Backend deletion warning:', backendErr);
      }
      
      await deleteUser(firebaseUser);
      
      memberLogout();
      
      return { success: true, message: 'Account deleted successfully' };
      
    } catch (err) {
      console.error('Account deletion error:', err);
      
      let errorMessage = 'Failed to delete account';
      
      switch (err.code) {
        case 'auth/requires-recent-login':
          errorMessage = 'Please sign in again before deleting your account';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        default:
          errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Get Firebase ID token
  const getIdToken = async (forceRefresh = false) => {
    if (!firebaseUser) return null;
    
    try {
      return await firebaseUser.getIdToken(forceRefresh);
    } catch (err) {
      console.error('Error getting ID token:', err);
      return null;
    }
  };

  // ============================================
  // INITIALIZATION
  // ============================================

  // Check URL for referral code on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref') || urlParams.get('referral');
    
    if (refCode) {
      saveReferralCode(refCode);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Load from storage and listen to Firebase auth state
  useEffect(() => {
    // Load existing sessions
    loadUserFromStorage();
    
    // Listen to Firebase auth state
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      firebaseUserRef.current = fbUser;
      
      // If Firebase user exists but no memberAuth, sync
      if (fbUser && !memberAuthRef.current) {
        const backendUser = await syncFirebaseUserWithBackend(fbUser);
        const memberData = firebaseUserToMemberAuth(fbUser, backendUser);
        
        localStorage.setItem(MEMBER_AUTH_KEY, JSON.stringify(memberData));
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userType', 'member');
        
        setMemberAuth(memberData);
        memberAuthRef.current = memberData;
        updateLastActivity();
      }
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [syncFirebaseUserWithBackend, updateLastActivity]);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    // Original state
    user: getActiveUser(),
    memberAuth,
    vendorAuth,
    // Firebase state
    firebaseUser,
    isNewUser,
    // Original methods
    login,
    register,
    logout,
    memberLogin,
    vendorLogin,
    memberLogout,
    vendorLogout,
    refreshUser,
    getActiveUser,
    // Firebase methods
    signInWithGoogle,
    signInWithEmail,
    registerWithEmail,
    resetPassword,
    deleteAccount,
    getIdToken,
    saveReferralCode,
    // State
    isLoggedIn: !!(memberAuth || vendorAuth),
    isEmailVerified: firebaseUser?.emailVerified || false,
    loading,
    error,
    // Helpers
    clearError: () => setError(null),
    clearNewUserFlag: () => setIsNewUser(false),
    // B16: Inactivity helpers
    resetActivityTimer,
    getTimeUntilLogout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;